/* nav-ia-005 — Role-aware sidebar (Claude Design "Luxury Palette").
   Behind the nav_v2 feature flag. Expanded 256px rail; grouped, collapsible
   sections with localStorage persistence; distinct per-item icons; WCAG-AA
   focus/active semantics. The rail stays dark navy in both themes (a fixed
   brand element), so its text tokens are pinned to their dark values.
   Visibility/labels/roles match the live sidebar (see navModelV2.js). */

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, LogOut, Crown, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { buildNavModelV2, firstAdminIndex } from "./navModelV2";

const PORTAL_LABEL = {
  client: "Member Portal",
  referrer: "Introducer Portal",
  admin: "Admin Portal",
  superuser: "Superuser Portal",
};

const OPEN_KEY = "es-nav-open";

// The rail is a permanent dark element — pin the semantic text/border tokens to
// their dark values so it reads correctly even when the app is in light theme.
const RAIL_DARK = {
  "--text-primary": "var(--es-cream)",
  "--text-body": "var(--es-platinum)",
  "--text-secondary": "rgba(229,228,226,0.72)",
  "--text-muted": "rgba(229,228,226,0.52)",
  "--text-faint": "rgba(229,228,226,0.38)",
  "--bg-hover": "var(--es-navy-600)",
  "--border-subtle": "rgba(229,228,226,0.10)",
  "--border-default": "rgba(229,228,226,0.16)",
  "--surface-gold": "rgba(234,179,8,0.10)",
  "--es-accent": "var(--es-gold-500)",
};

function scopedCss() {
  return `
  /* Rail positioning (responsive). ≥1024px: fixed 256px rail. <1024px:
     off-canvas drawer that slides in when data-open="true" (nav-ia-011). */
  .es2-rail { position: fixed; left: 0; top: 0; height: 100vh; height: 100dvh; width: 256px; z-index: 50; }
  @media (max-width: 1023px) {
    .es2-rail {
      transform: translateX(-100%);
      transition: transform var(--duration-normal) var(--ease-out);
      box-shadow: var(--shadow-lg); will-change: transform;
    }
    .es2-rail[data-open="true"] { transform: translateX(0); }
  }
  .es2-scrim { position: fixed; inset: 0; z-index: 49; background: var(--surface-overlay); }
  @media (min-width: 1024px) { .es2-scrim { display: none; } }

  .es2-link, .es2-head { transition: background var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out); }
  .es2-link:hover { background: var(--bg-hover); color: var(--text-body); }
  .es2-head:hover { background: var(--bg-hover); }
  .es2-link:focus-visible, .es2-head:focus-visible, .es2-signout:focus-visible {
    outline: 2px solid var(--es-accent); outline-offset: 2px; border-radius: var(--radius-sm);
  }
  .es2-body { display: grid; transition: grid-template-rows var(--duration-normal) var(--ease-out); }
  .es2-body > div { overflow: hidden; }
  @media (prefers-reduced-motion: reduce) {
    .es2-rail, .es2-link, .es2-head, .es2-body, .es2-chev { transition: none !important; }
  }`;
}

function readOpenState() {
  try {
    const raw = window.localStorage.getItem(OPEN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // Guard against null / arrays / corrupted values — must be a plain object,
    // else `key in openState` and spreads below would throw.
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function NavLink({ item, active, badges, onNavigate }) {
  const Icon = item.iconV2 || item.icon;
  const slug = item.testId || item.label.toLowerCase().replace(/\s+/g, "-");
  const badge = item.badgeKey ? badges?.[item.badgeKey] || 0 : 0;
  return (
    <Link
      to={item.href}
      data-testid={`nav-${slug}`}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
      className="es2-link"
      style={{
        display: "flex", alignItems: "center", gap: 11, padding: "9px 12px", marginBottom: 1,
        borderRadius: "var(--radius-sm)", textDecoration: "none",
        fontFamily: "var(--font-display)", fontSize: 13.5, fontWeight: 600,
        color: active ? "var(--es-gold-400)" : "var(--text-secondary)",
        background: active ? "var(--surface-gold)" : "transparent",
        boxShadow: active ? "inset 2px 0 0 var(--es-accent)" : "inset 2px 0 0 transparent",
      }}
    >
      <Icon size={18} style={{ color: active ? "var(--es-gold-400)" : "var(--text-muted)", flex: "0 0 auto" }} />
      <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
      {badge > 0 && (
        <span
          data-testid={`nav-badge-${slug}`}
          style={{ background: "var(--es-accent-secondary)", color: "#fff", fontSize: 10.5, fontWeight: 700, fontFamily: "var(--font-mono)", padding: "1px 7px", borderRadius: 999, lineHeight: 1.5 }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

function NavGroup({ group, activeRoute, open, onToggle, badges, onNavigate }) {
  const bodyId = `es2-grp-${group.key}`;
  const Icon = group.icon;

  if (group.kind === "flat") {
    return (
      <div style={{ marginBottom: 6 }}>
        {group.items.map((it) => (
          <NavLink key={it.href} item={it} active={activeRoute === it.href} badges={badges} onNavigate={onNavigate} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 4 }}>
      <button
        type="button"
        className="es2-head"
        aria-expanded={open}
        aria-controls={bodyId}
        data-testid={`nav-group-${group.key}`}
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "7px 10px",
          background: "transparent", border: "none", cursor: "pointer", borderRadius: "var(--radius-sm)",
          fontFamily: "var(--font-display)", textAlign: "left",
        }}
      >
        {Icon && <Icon size={14} style={{ color: "var(--text-faint)", flex: "0 0 auto" }} />}
        <span style={{ flex: 1, fontSize: 11, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: "var(--text-muted)" }}>
          {group.label}
        </span>
        <ChevronDown
          className="es2-chev"
          size={15}
          style={{ color: "var(--text-faint)", transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform var(--duration-normal) var(--ease-out)" }}
        />
      </button>
      <div id={bodyId} className="es2-body" style={{ gridTemplateRows: open ? "1fr" : "0fr" }} aria-hidden={!open}>
        <div>
          <div style={{ paddingBottom: 4 }}>
            {group.items.map((it) => (
              <NavLink key={it.href} item={it} active={activeRoute === it.href} badges={badges} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const SidebarV2 = ({ mobileOpen = false, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [kycActions, setKycActions] = useState(0);
  const [openState, setOpenState] = useState(readOpenState);
  const railRef = useRef(null);

  // Track the lg breakpoint: ≥1024px the rail is always visible; below it is an
  // off-canvas drawer (nav-ia-011). Drives the a11y hiding below.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // When the rail is off-canvas (mobile + closed) it must leave the a11y tree and
  // focus order so users can't tab into invisible links. `inert` has no stable
  // React 18 prop, so set it imperatively. On desktop it's always interactive.
  const offCanvas = !isDesktop && !mobileOpen;
  useEffect(() => {
    if (railRef.current) railRef.current.inert = offCanvas;
  }, [offCanvas]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/messages/unread-count");
        if (!cancelled) setUnread(r.data?.unread || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Admin badge — members awaiting a KYC / accreditation approval
  // (STARSEED-CAPMARKETS-071). Admin-only endpoint, so never fetched for
  // members; same filters as the Command Center tabs.
  const isPrivileged = user?.role === "admin" || user?.role === "superuser";
  useEffect(() => {
    if (!isPrivileged) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/command-center/admin/action-counts");
        if (!cancelled) setKycActions(r.data?.total || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isPrivileged]);

  // Mobile drawer: Esc closes it (nav-ia-011). Bound only while the *mobile*
  // drawer is open — never on desktop (where the rail is the permanent rail).
  useEffect(() => {
    if (!mobileOpen || isDesktop) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, isDesktop, onClose]);

  const groups = useMemo(() => buildNavModelV2(user), [user]);
  const adminIdx = firstAdminIndex(groups);

  const isOpen = (g) => (g.key in openState ? !!openState[g.key] : g.defaultOpen);
  const toggle = (key) =>
    setOpenState((prev) => {
      const current = key in prev ? prev[key] : groups.find((g) => g.key === key)?.defaultOpen;
      const next = { ...prev, [key]: !current };
      try { window.localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });

  const handleLogout = () => { logout(); navigate("/login"); };
  const portal = PORTAL_LABEL[user?.role] || "Portal";

  return (
    <>
      <style>{scopedCss()}</style>
      {mobileOpen && <div className="es2-scrim" aria-hidden="true" onClick={onClose} />}
      <aside
        ref={railRef}
        data-testid="sidebar-v2"
        className="es2-rail"
        data-open={mobileOpen ? "true" : "false"}
        aria-hidden={offCanvas || undefined}
        aria-label="Primary navigation"
        style={{
          ...RAIL_DARK,
          background: "var(--es-navy-800)", borderRight: "1px solid var(--border-subtle)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          fontFamily: "var(--font-body)",
        }}
      >
      {/* Brand header */}
      <Link
        to="/dashboard"
        onClick={onClose}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px", borderBottom: "1px solid var(--border-subtle)", textDecoration: "none" }}
      >
        <img src="/logo.png" alt="EchoSense Capital Club" style={{ width: 40, height: 40, objectFit: "contain", flex: "0 0 auto" }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 15, color: "var(--text-primary)", whiteSpace: "nowrap" }}>EchoSense Capital Club</div>
          <div style={{ fontSize: 10.5, color: "var(--text-muted)", letterSpacing: "0.04em" }}>{portal}</div>
        </div>
      </Link>

      {/* Navigation */}
      <nav aria-label="Primary" style={{ flex: 1, padding: "12px 12px", overflowY: "auto", overflowX: "hidden" }}>
        {groups.map((g, gi) => (
          <div key={g.key}>
            {gi === adminIdx && adminIdx > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 10px 8px" }}>
                <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--es-burgundy-300)" }}>Admin</span>
                <span style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
              </div>
            )}
            <NavGroup group={g} activeRoute={location.pathname} open={isOpen(g)} onToggle={() => toggle(g.key)} badges={{ unread, kyc_actions: kycActions }} onNavigate={onClose} />
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div style={{ padding: 16, borderTop: "1px solid var(--border-subtle)" }}>
        <div style={{ marginBottom: 12, paddingLeft: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span data-testid="sidebar-user-name" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-body)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {user?.full_name}
            </span>
            {user?.is_pioneer && user?.pioneer_number && (
              <span
                data-testid="sidebar-pioneer-badge"
                title={`Founding Pioneer №${String(user.pioneer_number).padStart(3, "0")}`}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--es-gold-400)" }}
              >
                <Crown size={10} /> №{String(user.pioneer_number).padStart(3, "0")}
              </span>
            )}
          </div>
          <div data-testid="sidebar-user-email" style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {user?.email}
          </div>
          {user?.internal_email && (
            <div data-testid="sidebar-internal-email" title="Your EchoSense Capital Club portal address" style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2, fontSize: 10.5, color: "var(--es-gold-300)" }}>
              <Mail size={10} style={{ flex: "0 0 auto" }} />
              <span style={{ fontFamily: "var(--font-mono)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.internal_email}</span>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          className="es2-signout w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="logout-button"
          style={{ background: "transparent", border: "1px solid var(--border-default)", color: "var(--text-secondary)" }}
        >
          <LogOut size={16} />
          Sign Out
        </Button>
      </div>
      </aside>
    </>
  );
};

export default SidebarV2;
